import React, { ReactNode } from 'react';
import styles from './EmailAnalytics.module.css';
import { ReactComponent as LeadMagnet } from 'assets/lead-generation-magnet.svg';
import { ReactComponent as OpenedEmailIcon } from 'assets/opened-email.svg';
import { ReactComponent as ClickedIcon } from 'assets/click.svg';
import { FallOutlined, RiseOutlined, SendOutlined } from '@ant-design/icons';
import { selectEmailAnalytics } from '@mm/state/selectors/botAnalyticsSelector';
import { useSelector } from 'react-redux';
import classNames from 'classnames';
import { calculateRate } from './EmailAnalytics';
import { EmailDeliverabilityScore } from '../../Integrations/NativeIntegrations/components/EmailDeliverabilityScore/EmailDeliverabilityScore';
import { useResolutionInfo } from '@mm/hooks';
import { MOBILE_SMALL } from '../../../../../constants/screenSize';

export const calculateGrowth = (
  current?: number | null,
  prev?: number | null,
) => {
  if (current && prev) {
    return ((current - prev) / prev) * 100;
  }
  return 0;
};

export const StatBox = ({
  label,
  icon,
  stat,
  note,
  growth = 0,
  allTime,
  withGrowth,
  className,
}: {
  label: string;
  icon?: ReactNode;
  note?: string;
  stat: string | ReactNode;
  growth?: number;
  allTime?: boolean;
  className?: string;
  withGrowth?: boolean;
}) => (
  <div className={classNames(styles.statBox, className)}>
    <div className={styles.heading}>
      <span className={styles.label}>{label}</span>
      {icon}
    </div>
    {note && <span className={styles.note}>{note}</span>}
    <div className={styles.stat}>{stat}</div>
    {withGrowth && growth !== 0 && (
      <div className={styles.footer}>
        <span
          className={classNames(styles.percentage, {
            [styles.percentageUp]: growth > 0,
            [styles.percentageDown]: growth < 0,
          })}
        >
          {growth > 0 ? (
            <RiseOutlined className={styles.rise} />
          ) : (
            <FallOutlined className={styles.fall} />
          )}
          {Math.abs(growth).toFixed(0)}%
        </span>
        <span className={styles.note}>vs prev 30 days</span>
      </div>
    )}
    {allTime && <span className={styles.note}>All Time All Triggers</span>}
  </div>
);

export const EmailSummaryStats = ({
  css,
  showEmailDeliverability,
  withXRayLeads = true,
  withGrowth = true,
}: {
  css?: {
    box?: string;
    deliverabilityContainer?: string;
    statsContainer?: string;
  };
  showEmailDeliverability?: boolean;
  withXRayLeads?: boolean;
  withGrowth?: boolean;
}) => {
  const emailAnalytics = useSelector(selectEmailAnalytics);
  const summaryStats = emailAnalytics?.summary?.stats;
  const currentStat = summaryStats?.[0]?.current;
  const prevStat = summaryStats?.[0]?.previous;
  const sent = Number(currentStat?.sent);

  const { screenWidth } = useResolutionInfo();
  const isMobile = screenWidth <= MOBILE_SMALL;

  return (
    <>
      {showEmailDeliverability && (
        <EmailDeliverabilityScore
          compactView={isMobile}
          css={{ container: css?.deliverabilityContainer }}
        />
      )}
      <div className={classNames(styles.statsContainer, css?.statsContainer)}>
        {withXRayLeads && (
          <StatBox
            className={css?.box}
            label="X-Ray Leads Captured"
            icon={<LeadMagnet className={styles.icon} />}
            stat={currentStat?.leads ? currentStat?.leads.toString() : '0'}
            withGrowth={withGrowth}
            allTime
          />
        )}
        <StatBox
          className={css?.box}
          label="Sent Emails"
          icon={<SendOutlined className={styles.icon} />}
          stat={currentStat?.sent ? currentStat?.sent.toString() : '0'}
          growth={calculateGrowth(currentStat?.sent, prevStat?.sent)}
          withGrowth={withGrowth}
        />
        <StatBox
          className={css?.box}
          label="Open Rate"
          icon={<OpenedEmailIcon className={styles.icon} />}
          stat={calculateRate(sent, currentStat?.open)}
          growth={calculateGrowth(currentStat?.open, prevStat?.open)}
          withGrowth={withGrowth}
        />
        <StatBox
          className={css?.box}
          label="Click Rate"
          icon={<ClickedIcon className={styles.icon} />}
          stat={calculateRate(sent, currentStat?.click)}
          growth={calculateGrowth(currentStat?.click, prevStat?.click)}
          withGrowth={withGrowth}
        />
      </div>
    </>
  );
};
